import { WeatherLoader } from '@fe/weather';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getWorker } from '../../../client/worker-container';
import { MessageType, PlaybackProgress, StartPlaybackResult } from '../../../services/actor-messages';
import { timeService } from '../../../services/time/time-service';
import { toSource } from '../../../services/weather/helpers';
import type { RootState } from '../slices';
import { addNotification, NotificationType } from '../slices/global-slice';
import { initPlaybackWeather } from '../slices/weather-slice';
import { AppThunk } from '../store';

export const playbackProgressChanged = createAction<PlaybackProgress>('playback/progress');
export const playbackSeekEnded = createAction('playback/seek-end');

export const startPlayback = createAsyncThunk(
    'playback/open',
    async (params: { kind: 'file'; directory: FileSystemDirectoryHandle | null; file: FileSystemFileHandle }, api) => {
        const state = api.getState() as RootState;
        if (state.global.recording.active) {
            api.dispatch(
                addNotification({
                    type: NotificationType.generalError,
                    customMessage: 'Cannot open playback file while recording is active'
                })
            );
            return api.fulfillWithValue({ ok: false, error: 'Cannot open playback file while recording is active' } satisfies Extract<
                StartPlaybackResult,
                { ok: false }
            >);
        }

        const result = await getWorker().actor.sendAsync({
            type: MessageType.playbackOpen,
            data: { request: { type: 'file', file: params.file } }
        });

        if (result.ok) {
            timeService.switchToPlayback(result.startTime);
            timeService.playback.setSpeed(1.0);
            timeService.playback.resume();

            let recordedProducts: number[] = [];
            if (params.kind === 'file' && params.directory) {
                recordedProducts = await findAllWeatherProductsInDirectory(params.directory);
                WeatherLoader.setSource({ type: 'file', directory: params.directory });
            }

            api.dispatch(initPlaybackWeather({ availableWeather: recordedProducts }));
            return api.fulfillWithValue(result);
        }
        return api.fulfillWithValue(result);
    }
);

export const changeSpeedPlayback = createAsyncThunk('playback/change-speed', async (params: { speed: number }) => {
    await getWorker().actor.sendAsync({ type: MessageType.playbackSetSpeed, data: params.speed });
    timeService.playback.setSpeed(params.speed);
});

export const pausePlayback = createAsyncThunk('playback/pause', async () => {
    await getWorker().actor.sendAsync({ type: MessageType.playbackStartPause, data: false });
    timeService.playback.pause();
});

export const resumePlayback = createAsyncThunk('playback/resume', async () => {
    await getWorker().actor.sendAsync({ type: MessageType.playbackStartPause, data: true });
    timeService.playback.resume();
});

export const stopPlayback = createAsyncThunk('playback/stop', async () => {
    const worker = getWorker();
    await worker.actor.sendAsync({ type: MessageType.playbackStop });
    timeService.switchToLive();
    const webServerAccessInfo = await worker.actor.sendAsync({ type: MessageType.weatherGetServerAccessInfo });
    WeatherLoader.setSource(toSource(webServerAccessInfo));
});

export const playbackProgress =
    (params: PlaybackProgress): AppThunk =>
    (dispatch) => {
        timeService.playback.setNow(params.currentTime, params.nextUpdateTime);
        dispatch(playbackProgressChanged(params));
    };

export const playbackSeekStart = createAsyncThunk('playback/seek-start', async (params: { toTime: number }) => {
    await getWorker().actor.sendAsync({ type: MessageType.playbackSeek, data: { toTime: params.toTime } });
});

export const playbackSeekCancel = createAsyncThunk('playback/seek-cancel', async () => {
    await getWorker().actor.sendAsync({ type: MessageType.playbackCancelSeek });
});

export const playbackSeekEnd = (): AppThunk => (dispatch) => {
    dispatch(playbackSeekEnded());
};

async function findAllWeatherProductsInDirectory(directory: FileSystemDirectoryHandle): Promise<number[]> {
    const products = new Set<number>();
    for await (const entry of directory.keys()) {
        if (!entry.endsWith('.fcp')) {
            continue;
        }

        const id = Number.parseInt(entry.slice(0, 4), 16);
        if (!Number.isFinite(id)) {
            continue;
        }

        products.add(id);
    }
    return [...products.values()];
}
